import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoaderIcon, Eye, EyeOff } from "lucide-react";
import Footer from '../components/Footer';
import axios from '../api/api';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL
  const [token, setToken] = useState('');

  useEffect(() => {
    // Parse token from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const resetToken = searchParams.get('token');

    if (!resetToken) {
      toast.error('Invalid or missing reset token');
      navigate('/forgotpassword');
    } else {
      setToken(resetToken);
    }

    document.documentElement.classList.add('reset-password-page');
    return () => {
      document.documentElement.classList.remove('reset-password-page');
    };
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Password strength validation
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/reset-password', { 
        token, 
        newPassword: password 
      });
      
      toast.success(response.data.message);
      setLoading(false);
      
      // Redirect to login after successful password reset
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.error || "An error occurred");
      setLoading(false);
    }
  };
  useEffect(() => {
    document.documentElement.classList.add('login-page');
    return () => {
    };
  }, []);
  return (
    <>
    <div className="p-4 flex flex-col items-start max-w-sm mx-auto h-dvh overflow-hidden pt-20 mb-20">
      {loading && (
        <div className="w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-background/80 z-50">
          <LoaderIcon className="w-5 h-5 animate-spin" />
        </div>
      )}
     
      <div className="flex items-center w-full py-8 border-b border-border/80">
        <h1 className="text-lg font-medium">Sellysta for ecommerce</h1>
      </div>

      <div className="flex flex-col items-start gap-y-6 py-8 w-full px-0.5">
        <h2 className="text-2xl font-semibold">
          Reset Your Password
        </h2>
        <p className="text-sm text-muted-foreground">
          Enter your new password. Make sure it's strong and unique.
        </p>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="space-y-2 w-full">
            <Label htmlFor="password">
              New Password
            </Label>
            <div className="relative w-full">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                disabled={loading}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full focus-visible:border-foreground"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={loading}
                className="absolute top-1 right-1 rounded-md"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 
                  <EyeOff className="w-4 h-4" /> : 
                  <Eye className="w-4 h-4" />
                }
              </Button>
            </div>
          </div>

          <div className="mt-4 space-y-2 w-full">
            <Label htmlFor="confirm-password">
              Confirm New Password
            </Label>
            <div className="relative w-full">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                disabled={loading}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full focus-visible:border-foreground"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={loading}
                className="absolute top-1 right-1 rounded-md"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? 
                  <EyeOff className="w-4 h-4" /> : 
                  <Eye className="w-4 h-4" />
                }
              </Button>
            </div>
          </div>

          <div className="mt-4 w-full">
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <LoaderIcon className="w-5 h-5 animate-spin" />
              ) : "Reset Password"}
            </Button>
          </div>
        </form>
      </div>

      <div className="flex items-start mt-auto border-t border-border/80 py-6 w-full">
        <p className="text-sm text-muted-foreground">
          Remember your password?{" "}
          <a href="/login" className="text-primary">
            Back to Login
          </a>
        </p>
      </div>
    </div>
    <Footer />
    </>
  );
};

export default ResetPassword;